import { getKeycloakToken } from './authShared'
import { fetchSseStream } from './sse'

// Kept open by the server through these states; it closes on any other status.
const ACTIVE_CONVERSION_STATUSES = new Set([
  'queued',
  'converting',
  'processing',
  'translating',
  'exporting',
  'conversion_cancelling',
  'translation_cancelling',
])

function parseConversionStatusEventData(rawData) {
  try {
    return JSON.parse(rawData)
  } catch {
    return null
  }
}

/**
 * Streams conversion status updates over SSE instead of polling.
 * Resolves once the stream ends (terminal status, doc-not-found, or abort).
 */
export async function streamConversionStatus(
  doc_id,
  { onStatus, onError, signal, token = getKeycloakToken() } = {}
) {
  const config = useRuntimeConfig()

  await fetchSseStream({
    url: `${config.public.documentsUrl}/v2/documents/${doc_id}/conversion-status/stream`,
    headers: {
      accept: 'text/event-stream',
      Authorization: `Bearer ${token}`,
    },
    signal,
    onMessage: (message) => {
      if (message.event === 'status') {
        const data = parseConversionStatusEventData(message.data)
        if (!data) {
          return
        }

        onStatus?.({
          status: data.conversion_status,
          progress: data.progress,
          failureReason: data.failure_reason ?? null,
        })

        if (!ACTIVE_CONVERSION_STATUSES.has(data.conversion_status)) {
          return false
        }

        return
      }

      if (message.event === 'error') {
        const data = parseConversionStatusEventData(message.data) || {}
        const isDocDeleted = data.detail === 'Document not found.'

        onError?.(data.detail || 'Conversion status stream error.', {
          terminal: isDocDeleted,
        })

        if (isDocDeleted) {
          return false
        }
      }
    },
  })
}

export async function getAllDocuments(search = '', token = getKeycloakToken()) {
  const config = useRuntimeConfig()
  const searchParam = search
    ? `?search_query=${encodeURIComponent(search)}`
    : ''
  const response = await $fetch(
    `${config.public.documentsUrl}/v2/documents${searchParam}`,
    {
      method: 'GET',
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  )

  return response
}

export async function getDocumentInfo(doc_id, token = getKeycloakToken()) {
  try {
    const config = useRuntimeConfig()
    const response = await $fetch(
      `${config.public.documentsUrl}/v2/documents/${doc_id}/metadata`,
      {
        method: 'GET',
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    )
    return response
  } catch (error) {
    console.error('Error geting the document', error)
  }
}

export async function translateDocument(
  doc_id,
  options,
  token = getKeycloakToken()
) {
  try {
    const config = useRuntimeConfig()
    const response = await $fetch(
      `${config.public.documentsUrl}/v2/documents/translate`,
      {
        method: 'POST',
        body: {
          doc_id: doc_id,
          options: options,
        },
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Laniqo-Client': 'ui',
        },
      }
    )
    return response?.doc_id || response
  } catch (error) {
    console.error('Error during translation:', error)
  }
}

export async function postDocument(
  file,
  token = getKeycloakToken(),
  pdf_converter_type = null,
  pdf_processing_mode = null
) {
  try {
    const formData = new FormData()
    formData.append('new_doc', file)
    const config = useRuntimeConfig()
    const params = new URLSearchParams()
    if (pdf_converter_type) params.set('pdf_converter_type', pdf_converter_type)
    if (pdf_processing_mode)
      params.set('pdf_processing_mode', pdf_processing_mode)
    const query = params.toString() ? `?${params.toString()}` : ''
    const response = await $fetch(
      `${config.public.documentsUrl}/v2/documents${query}`,
      {
        method: 'POST',
        body: formData,
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Laniqo-Client': 'ui',
        },
      }
    )
    return response.doc_id
  } catch (error) {
    if (error.response.status === 413) {
      return error.response.status
    } else {
      console.error('Error posting the document', error)
    }
  }
}

export async function getConversionStatus(doc_id, token = getKeycloakToken()) {
  try {
    const config = useRuntimeConfig()
    const statusResponse = await $fetch(
      `${config.public.documentsUrl}/v2/documents/${doc_id}/conversion-status`,
      {
        method: 'GET',
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    )
    return {
      status: statusResponse.conversion_status,
      progress: statusResponse.progress,
    }
  } catch {
    return { status: 'failed', progress: 0 }
  }
}

export async function exportDocument(doc_id, token = getKeycloakToken()) {
  try {
    const config = useRuntimeConfig()
    const response = await $fetch(
      `${config.public.documentsUrl}/v2/documents/${doc_id}/export`,
      {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Laniqo-Client': 'ui',
        },
      }
    )
    return response
  } catch (error) {
    console.error('Error exporting the document', error)
  }
}

export async function deleteDocument(doc_id, token = getKeycloakToken()) {
  try {
    const config = useRuntimeConfig()
    const res = await $fetch(
      `${config.public.documentsUrl}/v2/documents/${doc_id}`,
      {
        method: 'DELETE',
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    )
    return res
  } catch (error) {
    console.error('Error deleting the document', error)
  }
}

export async function getSegments(
  doc_id,
  limit,
  offset,
  token = getKeycloakToken(),
  with_qe_errors = false,
  search_query = ''
) {
  const config = useRuntimeConfig()
  const searchParam = search_query
    ? `&search_query=${encodeURIComponent(search_query)}`
    : ''
  const response = await $fetch(
    `${config.public.documentsUrl}/v2/segments/get?doc_id=${doc_id}&limit=${limit}&offset=${offset}&with_qe_errors=${with_qe_errors}${searchParam}`,
    {
      method: 'GET',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return response
}

export async function countSegments(doc_id, token = getKeycloakToken()) {
  try {
    const config = useRuntimeConfig()
    const response = await $fetch(
      `${config.public.documentsUrl}/v2/segments/count?doc_id=${doc_id}`,
      {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    )

    return response
  } catch (error) {
    console.error('Error getting the segment count', error)
  }
}

export async function replaceSegments(
  doc_id,
  replace_data,
  token = getKeycloakToken()
) {
  try {
    const config = useRuntimeConfig()
    const response = await $fetch(
      `${config.public.documentsUrl}/v2/segments/replace`,
      {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: {
          doc_id: doc_id,
          replacements: replace_data,
        },
      }
    )

    return response
  } catch (error) {
    console.error('Error replacing the segments', error)
    return error
  }
}

export async function downloadFileFunction(
  doc_id,
  file_name,
  editable = false,
  source = false,
  export_format = null,
  token = getKeycloakToken()
) {
  try {
    const config = useRuntimeConfig()
    const exportFormatParam = export_format
      ? `&export_format=${export_format}`
      : ''
    const response = await fetch(
      `${config.public.documentsUrl}/v2/documents/${doc_id}?editable=${editable}&source=${source}${exportFormatParam}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = file_name
    document.body.appendChild(link)
    link.click()

    setTimeout(() => {
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }, 100)

    return true
  } catch (error) {
    console.error('Error downloading file:', error)
    return false
  }
}

export function buildFilenameWithFormat(filename, format) {
  if (!format) {
    return filename
  }
  const parts = filename.split('.')
  if (parts.length > 1) {
    parts.pop()
  }
  return `${parts.join('.')}.${format}`
}

export async function renameFile(doc_id, new_name, token = getKeycloakToken()) {
  try {
    const config = useRuntimeConfig()
    const response = await $fetch(
      `${config.public.documentsUrl}/v2/documents/${doc_id}/name?name=${encodeURIComponent(new_name)}`,
      {
        method: 'PUT',
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    )
    return response
  } catch (error) {
    console.error('Error renaming the file', error)
    throw error
  }
}

export async function getSupportedFormats(token = getKeycloakToken()) {
  try {
    const config = useRuntimeConfig()
    const response = await $fetch(
      `${config.public.documentsUrl}/v2/capabilities/supported-formats`,
      {
        method: 'GET',
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    )
    return response
  } catch (error) {
    console.error('Error getting the supported formats', error)
    throw error
  }
}

export async function retryTranslation(doc_id, token = getKeycloakToken()) {
  try {
    const config = useRuntimeConfig()
    const response = await $fetch(
      `${config.public.documentsUrl}/v2/documents/${doc_id}/retranslate`,
      {
        method: 'POST',
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Laniqo-Client': 'ui',
        },
      }
    )
    return response
  } catch (error) {
    console.error('Error retrying the translation', error)
    throw error
  }
}

export async function startQualityEvaluation(
  doc_id,
  token = getKeycloakToken()
) {
  try {
    const config = useRuntimeConfig()
    const response = await $fetch(
      `${config.public.documentsUrl}/v2/quality-estimation/${doc_id}/run`,
      {
        method: 'POST',
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    )
    return response
  } catch (error) {
    console.error('Error starting the quality evaluation', error)
    throw error
  }
}

export async function getQualityEvaluationStatus(
  doc_id,
  token = getKeycloakToken()
) {
  try {
    const config = useRuntimeConfig()
    const response = await $fetch(
      `${config.public.documentsUrl}/v2/quality-estimation/${doc_id}/status`,
      {
        method: 'GET',
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    )
    return response
  } catch (error) {
    console.error('Error getting the quality evaluation status', error)
    throw error
  }
}

export async function deleteQualityEstimationError(
  error_id,
  token = getKeycloakToken()
) {
  try {
    const config = useRuntimeConfig()
    const response = await $fetch(
      `${config.public.documentsUrl}/v2/quality-estimation/errors/${error_id}`,
      {
        method: 'DELETE',
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    )
    return response
  } catch (error) {
    console.error('Error deleting the quality estimation error', error)
    throw error
  }
}

export async function cancelQualityEvaluation(
  doc_id,
  options = {},
  token = getKeycloakToken()
) {
  try {
    const config = useRuntimeConfig()
    const response = await $fetch(
      `${config.public.documentsUrl}/v2/quality-estimation/${doc_id}/cancel`,
      {
        method: 'POST',
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        ...options,
      }
    )
    return response
  } catch (error) {
    console.error('Error canceling the quality evaluation', error)
    throw error
  }
}

export async function resumeQualityEvaluation(
  doc_id,
  token = getKeycloakToken()
) {
  try {
    const config = useRuntimeConfig()
    const response = await $fetch(
      `${config.public.documentsUrl}/v2/quality-estimation/${doc_id}/resume`,
      {
        method: 'POST',
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    )
    return response
  } catch (error) {
    console.error('Error resuming the quality evaluation', error)
    throw error
  }
}
